import React from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, AlertCircle, Lock } from 'lucide-react';
import { Card } from '../ui/Card';
import { PremiumGate } from '../PremiumGate';
import { UserSentimentHistoryItem, SentimentVoteOption } from '../../types/sentiment';
import { CategoryHelpers } from '../../types/stockCategories';
import { cn } from '../../utils/cn';
import { useAccessControl } from '../../contexts/AccessControlContext';

interface SentimentHistoryProps {
  history: UserSentimentHistoryItem[];
  isLoading: boolean;
  error: string | null;
  isPremium: boolean;
  className?: string;
}

interface VoteBadgeProps {
  vote: SentimentVoteOption;
  className?: string;
}

const VoteBadge: React.FC<VoteBadgeProps> = ({ vote, className }) => {
  const config = {
    buy: {
      icon: TrendingUp,
      label: 'Buy',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      textColor: 'text-green-700 dark:text-green-400',
      iconColor: 'text-green-600 dark:text-green-500'
    },
    hold: {
      icon: Minus,
      label: 'Hold',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      textColor: 'text-yellow-700 dark:text-yellow-400',
      iconColor: 'text-yellow-600 dark:text-yellow-500'
    },
    sell: {
      icon: TrendingDown,
      label: 'Sell',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      textColor: 'text-red-700 dark:text-red-400',
      iconColor: 'text-red-600 dark:text-red-500'
    }
  };

  const voteConfig = config[vote];
  const Icon = voteConfig.icon;

  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium',
      voteConfig.bgColor,
      voteConfig.textColor,
      className
    )}>
      <Icon className={cn('h-4 w-4', voteConfig.iconColor)} />
      <span>{voteConfig.label}</span>
    </div>
  );
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const SentimentHistory: React.FC<SentimentHistoryProps> = ({
  history,
  isLoading,
  error,
  isPremium,
  className
}) => {
  const { shouldShowSentimentHistory, premiumMode } = useAccessControl();

  // If sentiment history UI is disabled (FREE mode or explicitly hidden),
  // show content directly without premium gate
  const shouldBypassPremiumGate = !shouldShowSentimentHistory || premiumMode === 'FREE';

  // Premium gate with loading state
  const premiumFallback = (
    <Card className={cn('p-6 text-center border-primary/20', className)}>
      <Lock className="h-12 w-12 mx-auto mb-3 text-primary/60" />
      <h3 className="text-lg font-semibold text-foreground mb-2">
        Premium Feature
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Upgrade to Premium to view your sentiment voting history and track all your stock predictions.
      </p>
    </Card>
  );

  const loadingComponent = (
    <Card className={cn('p-6', className)}>
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
        <p className="text-sm text-muted-foreground">Loading subscription status...</p>
      </div>
    </Card>
  );

  const sentimentContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <Card className={cn('p-6', className)}>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Loading sentiment history...</p>
          </div>
        </Card>
      );
    }

    // Error state
    if (error) {
      return (
        <Card className={cn('p-6 text-center border-destructive/50', className)}>
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-destructive font-medium mb-2">Failed to load sentiment history</p>
          <p className="text-xs text-muted-foreground">{error}</p>
        </Card>
      );
    }

    // Empty state
    if (history.length === 0) {
      return (
        <Card className={cn('p-6 text-center', className)}>
          <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No sentiment votes yet. Start voting on stocks to track your predictions!
          </p>
        </Card>
      );
    }

    // Desktop table view
    const desktopView = (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Stock
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Company
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Vote
            </th>
            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map((item, index) => {
            const categoryConfig = CategoryHelpers.getConfig(item.category);

            return (
              <tr
                key={`${item.stockSymbol}-${item.votedAt}-${index}`}
                className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-foreground">
                      {item.stockSymbol}
                    </span>
                    {categoryConfig && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: `${categoryConfig.color}20`,
                          color: categoryConfig.color
                        }}
                      >
                        {categoryConfig.icon}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-muted-foreground">
                    {item.companyName}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <VoteBadge vote={item.userVote} />
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item.votedAt)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

    // Mobile card view
    const mobileView = (
    <div className="md:hidden space-y-3">
      {history.map((item, index) => {
        const categoryConfig = CategoryHelpers.getConfig(item.category);

        return (
          <Card
            key={`${item.stockSymbol}-${item.votedAt}-${index}`}
            className="p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono font-semibold text-foreground">
                    {item.stockSymbol}
                  </span>
                  {categoryConfig && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: `${categoryConfig.color}20`,
                        color: categoryConfig.color
                      }}
                    >
                      {categoryConfig.icon}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {item.companyName}
                </p>
              </div>
              <VoteBadge vote={item.userVote} />
            </div>
            <div className="text-xs text-muted-foreground">
              {formatDate(item.votedAt)}
            </div>
          </Card>
        );
      })}
    </div>
  );

    return (
      <Card className={cn('p-6', className)}>
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Sentiment Voting History
        </h3>
        {desktopView}
        {mobileView}
      </Card>
    );
  };

  // If premium gate is bypassed (FREE mode or hidden), show content directly
  if (shouldBypassPremiumGate) {
    return sentimentContent();
  }

  // Otherwise, wrap in premium gate
  return (
    <PremiumGate
      fallback={premiumFallback}
      loadingComponent={loadingComponent}
    >
      {sentimentContent()}
    </PremiumGate>
  );
};

export default SentimentHistory;
